import CsrfTokenStore from 'stores/CsrfTokenStore';
import CsrfTokenActionCreator from 'actions/CsrfTokenActionCreator';
import ReduxStoreStore from 'stores/ReduxStoreStore';
import StubResponses from 'sdk/StubResponses';
import { addError } from 'actions/shared';

const Assets = {
  translations: (locale, assetName) => `https://d8aa01cdolqj7.cloudfront.net/strings/${locale}/${assetName}.json`,
};

const Resource = {
  account: '/api/account',
  accountPool: '/api/accountPool',
  author: amazonAuthorId => `/api/author/${amazonAuthorId}`,
  authorInsightsFeed: '/api/v1/authorInsightsFeed',
  atAGlance: isAdmin => `${isAdmin ? '/admin' : ''}/api/v1/${isAdmin ? 'adminReports' : 'reports'}/atAGlance`,
  bio: bioId => (bioId ? `/api/bio/${bioId}` : '/api/bio'),
  bios: amazonAuthorId => `/api/bios?amazonAuthorId=${amazonAuthorId}`,
  book: '/api/book',
  bookAdd: '/api/bookAdd',
  bookSearch: '/api/search/book',
  customerPreferences: '/api/v1/reports/customerPreferences',
  customerReviews: '/api/customerReview',
  kdpContributorClaims: status => `/api/contributorsForKdpPublisher/${status}`,
  adminKdpContributorClaims: status => `/admin/api/adminContributorsForKdpPublisher/${status}`,
  emailConfirmation: '/api/emailConfirmation',
  errorLogs: '/api/errorLogs',
  feedImage: (amazonAuthorId, feedImageId) => `/api/${amazonAuthorId}/feedMedia/${feedImageId}`,
  feedMedia: amazonAuthorId => `/api/${amazonAuthorId}/feedMedia`,
  feedVideo: (amazonAuthorId, feedVideoId) => `/api/${amazonAuthorId}/feedMedia/${feedVideoId}`,
  helpNode: nodeId => `/api/v1/help/${nodeId}`,
  helpNodeHierarchy: '/api/v1/help',
  identity: '/api/identity',
  legacyAccountStatus: amazonAuthorId => `/api/legacyAccountStatus/${amazonAuthorId}`,
  lastVisitedDate: '/api/lastVisitedDate',
  preferences: customerId => `/api/preferences/${customerId}`,
  profileImage: amazonAuthorId => `/api/profileImage/${amazonAuthorId}`,
  salesRank: '/api/v2/salesRank',
  salesRankV3: '/api/v3/salesRank',
  terms: '/api/terms',
  titleset: titlesetAsin => `/api/titleset/${titlesetAsin}`,
  titlesetBindings: titlesetAsin => `/api/titleset/bindings/${titlesetAsin}`,
  titlesetsFaceout: '/api/titlesetsFaceout',
  titlesetSearch: '/api/search/titleset',
  //  Below APIs are deprecated. TT: https://t.corp.amazon.com/0406825202
  miniCustomerProfile: '/api/miniCustomerProfile',
  verifiedProfileEntities: '/api/verifiedProfileEntities',
};

const Functions = {
  claimIdentity: '/api/functions/claimIdentity',
  completeAccountForLegacyUser: '/api/functions/completeAccountForLegacyUser',
  confirmIdentity: '/api/functions/confirmIdentity',
  generateUploadParameters: '/api/functions/generateUploadParameters',
  handleEmailConfirmation: '/api/functions/handleEmailConfirmation',
  respondToKdpContributorClaim: '/api/functions/respondToKdpContributorClaim',
  updatePendingBookAdds: amazonAuthorId => `/api/functions/updatePendingBookAdds?amazonAuthorId=${amazonAuthorId}`,
  vanityUrl: isTest => `api/functions/vanityUrl${isTest ? '/test' : ''}`,
};

const Method = {
  get: 'GET',
  put: 'PUT',
  post: 'POST',
  delete: 'DELETE',
};

const asset = assetUrl => new Promise((resolve, reject) => {
  $.ajax({
    url: assetUrl,
    dataType: 'json',
    crossDomain: true,
    success(data, status) {
      resolve({ data, status });
    },
    error(request) {
      const error = new Error(request.responseText);
      error.status = request.status;
      reject(error);
    },
  });
});

/* If method is GET, params should be the query parameters to append to the URL.
* If method is a POST/PUT/DELETE, params should be the request body.
* In general POSTs shouldn't also require query parameters
* (just put it in the body!), however there are exceptions
* (like how Ingress specifically needs 'sif_profile=author_service')
* so in those cases use additionalQueryParam */
const api = (resource, method, params, headers = {}, additionalQueryParam = '', handle404AsError = false, dataType = 'json') => {
  if (!resource) {
    throw new Error('Resource not defined.');
  }

  if (StubResponses.contains(resource, method)) {
    return StubResponses.fetch(resource, method);
  }
  const newHeaders = { ...headers };
  const { token } = CsrfTokenStore.getState();
  if (token) {
    newHeaders['X-Csrf-Token'] = token;
  }

  /* If the request is a GET request, $.ajax will
  turn the params into a query string and append
  them to the URL, otherwise they will be included
  in the body, so we need to JSON stringify them. */
  let newParams = { ...params };
  if (method !== Method.get) {
    newParams = JSON.stringify(newParams);
  }

  const url = additionalQueryParam === '' ? resource : `${resource}?${additionalQueryParam}`;

  const baseAjaxParams = {
    url,
    data: newParams,
    dataType,
    contentType: 'application/json',
    method,
    headers: newHeaders,
    // http://api.jquery.com/jQuery.param/#jQuery-param-obj-traditional
    traditional: true,
  };
  return new Promise((resolve, reject) => {
    const ajaxCallbackParams = {
      success(data, status, xhr) {
        resolve({ data, status, xhr });
      },
      error(xhr) {
        // Make the error available in Redux so components
        // can respond accordingly
        const { reduxStore } = ReduxStoreStore.getState();
        if (reduxStore && reduxStore.dispatch) {
          reduxStore.dispatch(addError(xhr));
        }

        // If we receive a 401 and the unauthenticated header, refresh the page
        if (xhr.status === 401 && xhr.getResponseHeader('aa-ajx-unauth')) {
          window.location.reload();
        }
        // If we receive a 419 it means the user's CSRF token has expired.
        if (xhr.status === 419) {
          CsrfTokenActionCreator.refreshToken().then(() => {
            $.ajax($.extend(baseAjaxParams, ajaxCallbackParams));
          }).then(resolve());
        }

        // 404s may not be errors, depending upon the API used
        if (!handle404AsError && xhr.status === 404) {
          resolve({ status: xhr.status });
        }
        const error = new Error(xhr.responseText);
        error.status = xhr.status;
        error.data = xhr.responseText;
        reject(error);
      },
    };
    $.ajax($.extend(baseAjaxParams, ajaxCallbackParams));
  });
};

export {
  Assets, Resource, Functions, Method, asset, api,
};
